import { Component } from 'react';
import last from 'lodash/last';
import throttle from 'lodash/throttle';

import {
  useIsParentMobile,
  useCanStartChat,
  clearSessionKey,
  loadSessionKey,
  loadWasConnected,
  loadWasOpen,
  saveSessionKey,
  saveWasConnected,
  saveWasOpen
} from '@belong/chat-utils';

import { logger } from '@belong/logging/logger';
import { isError } from '@belong/api';
import { IInitiateChatResponse, IInitiateChatResponseV2 } from '@belong/types/api/chat';
import ChatAPI from '../api';

import { createChatSession } from '../lib/createChatSession';
import { getAutoResponse } from '../lib/constants';
import { parseChatDetails, parseChatDetailsV2 } from '../lib/parseChatDetails';
import {
  withoutTriggeringMessages,
  findLastIncomingMessage,
  customerMessageCount,
  findLastReceiptableMessage,
  isAgentParticipating,
  isWaitingForAgentToJoin
} from '../lib/transcript';
import {
  isAttachmentRejected,
  isIncoming,
  isIncomingTriggerMessage,
  isInteractive,
  isInteractiveMessage,
  isListPickerSelection,
  isOutgoingAttachment
} from '../lib/itemCheckers';
import { redirectParentTo } from '../lib/redirectParentTo';
import { findParentNextRouter } from '../lib/findParentNextRouter';
import { useIsSessionsV2Enabled } from '../lib/useIsSessionsV2Enabled';
import { Chat, focusOnOpenButton, focusOnTitle } from './Chat';
import CONTENT from './content.json';

// NOTE: This import needs to be here above the import('amazon-connect-chatjs')
// to ensure that TypeScript extends/overrides the types in the correct order.
import '../types/amazon-types-extended.d';

type SendAttachmentResultWithRequest = connect.SendAttachmentResult & {
  request: { params: { AttachmentIds: string[] } };
};

const isExtendedResult = (result?: connect.SendAttachmentResult): result is SendAttachmentResultWithRequest =>
  !!result && 'request' in result;

type ChatContainerProps = {
  router?: any;
  canStartChat?: boolean;
  isFullScreen?: boolean;
  isSessionsV2Enabled: boolean;
};

type ChatContainerState = {
  isConnected?: boolean;
  isOpen?: boolean;
  isScriptReady?: boolean;
  isInitiating?: boolean;
  isAgentTyping?: boolean;
  errorMessage?: string;
  transcript?: connect.ChatTranscriptItem[];
  showQuitConfirmation?: boolean;
  showFeedback?: boolean;
  contactId?: string;
  unreadCount?: number; // Indicates the number of messages received while the Chat was minimized. Is not related to the "read receipts" feature
  attachmentScanning?: string;
};

export class ChatContainer extends Component<ChatContainerProps, ChatContainerState> {
  /* eslint-disable react/static-property-placement */
  static displayName = 'ChatContainer';
  static defaultProps: Partial<ChatContainerProps> = { canStartChat: true };
  /* eslint-enable react/static-property-placement */

  chatSession?: connect.CustomerChatSession;
  typingTimeout?: number;

  readonly state: ChatContainerState = {};

  handleClickOpen = () => {
    const { isOpen, isConnected, isInitiating } = this.state;
    if (!isOpen && !isConnected && !isInitiating) {
      this.initiateChat();
    }
    this.setState({ isOpen: true, unreadCount: 0 }, focusOnTitle);
  };

  handleClickMinimize = () => {
    this.setState({ isOpen: false }, focusOnOpenButton);
  };

  handleClickClose = () => {
    if (this.state.isConnected) {
      this.setState({ showQuitConfirmation: true });
    } else {
      this.setState({ isOpen: false }, focusOnOpenButton);
    }
  };

  handleQuitConfirm = () => {
    if (this.state.isConnected) {
      this.chatSession?.disconnectParticipant();
      this.handleEnded();
    }
    this.setState({ showQuitConfirmation: false });
  };

  handleQuitCancel = () => {
    this.setState({ showQuitConfirmation: false });
  };

  handleSendMessage = (message: string) => {
    if (message) {
      this.chatSession
        ?.sendMessage({
          contentType: 'text/plain',
          message
        })
        .then(() => this.setState({ errorMessage: undefined }))
        .catch(this.handleSendError);
    }
  };

  handleDownloadAttachment = async (attachmentId: string) => {
    try {
      return await this.chatSession?.downloadAttachment({ attachmentId });
    } catch (e) {
      return Promise.reject(e);
    } finally {
      this.setState({ attachmentScanning: undefined });
    }
  };

  handleSendAttachment = async (attachmentFile: File) => {
    const result = await this.chatSession?.sendAttachment({
      attachment: attachmentFile
    });

    if (isExtendedResult(result)) {
      const [attachmentId] = result.request.params.AttachmentIds;
      this.setState({ attachmentScanning: attachmentId });
    }

    return result;
  };

  sendReadEvent = (messageId: string) => {
    this.chatSession?.sendEvent({
      contentType: 'application/vnd.amazonaws.connect.event.message.read',
      content: JSON.stringify({ messageId })
    });
  };

  focusOnLatestMessage = () => {
    // a11y assistance: move the user's focus to the next relevant content,
    // but only for ListPickers, which are shown after click/touch-based interactions,
    // not for normal messages, which are typing-based interactions
    const filteredTranscript = withoutTriggeringMessages(this.state.transcript || []);
    const latestMessage = last(filteredTranscript);
    if (latestMessage) {
      const shouldTakeFocus =
        isInteractiveMessage(latestMessage) ||
        isListPickerSelection(latestMessage, filteredTranscript.length - 1, filteredTranscript);
      if (shouldTakeFocus) {
        document.getElementById(`message_${latestMessage.Id}`)?.focus();
      }
    }
  };

  handleMessage = (messageEvent: connect.ChatMessageEvent) => {
    const message = messageEvent.data;
    // Avoid displaying duplicates
    if (this.state.transcript?.some(existingMessage => existingMessage.Id === message.Id)) {
      return;
    }
    // Custom behavior: back-end wants to send a special message,
    // which triggers the user to send a special message,
    // which should begin the chat flow.
    if (isIncomingTriggerMessage(message)) {
      const response = getAutoResponse(message.Content);
      this.handleSendMessage(response);
    }
    if (isIncoming(message)) {
      this.hideTypingIndicator();
      if (!this.state.isOpen) {
        this.setState(({ unreadCount = 0 }) => ({ unreadCount: unreadCount + 1 }));
      }
    }

    // If there is an attachment from a customer, and it matches the ID of the attachment we are currently
    // scanning, if rejected, we want to unset attachmentScanning. If it is approved, this will happen in
    // after the downloadAttachment
    if (isOutgoingAttachment(message)) {
      const [attachment] = message.Attachments ?? [];
      if (attachment && this.state.attachmentScanning === attachment.AttachmentId && isAttachmentRejected(attachment)) {
        this.setState({ attachmentScanning: undefined });
      }
    }

    this.setState(
      ({ transcript }) => ({
        transcript: [...(transcript || []), message],
        isInitiating: false,
        errorMessage: undefined
      }),
      this.focusOnLatestMessage
    );
  };

  handleTyping = (event: connect.ChatTypingEvent) => {
    if (isIncoming(event.data)) {
      this.showTypingIndicator();
    }
  };

  handleUserTyping = throttle(
    () => {
      this.chatSession?.sendEvent({ contentType: 'application/vnd.amazonaws.connect.event.typing' });
    },
    4000,
    { leading: true, trailing: false }
  );

  handleSendError = ({ type }) => {
    if (type === 'NetworkingError' && this.state.isConnected) {
      this.setState({ errorMessage: CONTENT.connectionLost });
    }
  };

  handleConnectionEstablished = () => {
    this.setState({
      errorMessage: undefined,
      isConnected: true
    });
    this.getTranscript();
  };

  handleConnectionBroken = () => {
    this.setState({
      errorMessage: CONTENT.connectionBroken,
      isConnected: false
    });
  };

  handleConnectionLost = () => {
    this.setState({
      errorMessage: CONTENT.connectionLost,
      isConnected: false
    });
  };

  handleEnded = () => {
    clearSessionKey();
    this.setState({ isConnected: false, unreadCount: 0 });
    if (customerMessageCount(this.state.transcript || []) >= 2) {
      this.setState({ showFeedback: true });
    }
  };

  handlePickerItemSelect = (item: connect.ListPickerItem, redirect = true) => {
    this.chatSession
      ?.sendMessage({
        contentType: 'text/plain',
        message: item.title
      })
      .then(() => {
        // TODO(ken): align once capitalization is updated on back-end
        const url = item.url || item.Url;
        if (url && redirect) {
          redirectParentTo(url);
        }
        this.setState({ errorMessage: undefined });
      })
      .catch(this.handleSendError);
  };

  showTypingIndicator = () => {
    this.setState({ isAgentTyping: true });
    clearTimeout(this.typingTimeout);
    this.typingTimeout = window.setTimeout(this.hideTypingIndicator, 8000);
  };

  hideTypingIndicator = () => {
    clearTimeout(this.typingTimeout);
    this.setState({ isAgentTyping: false });
  };

  getTranscript = () => {
    this.chatSession
      ?.getTranscript({ maxResults: 100 })
      .then(({ data }) => {
        if (data?.Transcript?.length) {
          this.setState({
            transcript: data?.Transcript,
            isInitiating: false
          });
        }
      })
      .catch(error => {
        this.setState({
          errorMessage: CONTENT.chat.genericError,
          isInitiating: false
        });
        logger.error(`Failed to get chat transcript: ${error?.message}`);
      });
  };

  initiateChat = async () => {
    const sessionKey = loadSessionKey();
    this.setState({
      isInitiating: true,
      errorMessage: undefined,
      showFeedback: false,
      transcript: [],
      unreadCount: 0
    });

    ChatAPI.initiateChat(sessionKey, this.props.isSessionsV2Enabled)
      .then(result => {
        const { response, body } = result;
        if (!response.ok || isError(body)) {
          throw new Error();
        } else {
          const { startChatResult } = body.data;

          const startChatDetails = this.props.isSessionsV2Enabled
            ? parseChatDetailsV2(startChatResult as IInitiateChatResponseV2['data']['startChatResult'])
            : parseChatDetails(startChatResult as IInitiateChatResponse['data']['startChatResult']);

          this.setState({ contactId: startChatDetails.contactId });
          this.createChatSession(startChatDetails);
          saveSessionKey(JSON.stringify(body));
        }
      })
      .catch(() => {
        this.setState({
          errorMessage: CONTENT.chat.genericError,
          isInitiating: false
        });
        clearSessionKey();
      });
  };

  createChatSession = (startChatDetails: connect.ChatDetailsInput) => {
    // TODO: accommodate for vite's dev mode double mounting react, either here or in initiateChat (or didMount).
    const session = createChatSession(startChatDetails);
    session.onConnectionEstablished(this.handleConnectionEstablished);
    session.onConnectionBroken(this.handleConnectionBroken);
    session.onConnectionLost(this.handleConnectionLost);
    session.onEnded(this.handleEnded);
    session.onMessage(this.handleMessage);
    session.onTyping(this.handleTyping);
    session.connect();
    this.chatSession = session;
  };

  handleRouteChangeStart = () => {
    if (this.props.isFullScreen) {
      this.handleClickMinimize();
    }
  };

  handleRouteChangeComplete = () => {
    // NextJS is sometimes interfering with the render cycle, unsure as to what is actually happening.
    // If a message is received at the same moment as a Next route change, the state update which adds
    // the new message does not complete and the message is not shown until the next render cycle.
    this.forceUpdate();
  };

  markLatestAsRead = () => {
    const latestReceiptableMessage = findLastReceiptableMessage(this.state.transcript || []);
    if (latestReceiptableMessage) {
      this.sendReadEvent(latestReceiptableMessage.Id);
    }
  };

  handleMessageReceipts = () => {
    const { isOpen } = this.state;
    const isUserWatching = isOpen && document.hasFocus();
    if (isUserWatching) {
      this.markLatestAsRead();
    }
  };

  restoreFromStorage = () => {
    const { isFullScreen } = this.props;
    const wasConnected = loadWasConnected();
    const wasOpen = loadWasOpen();

    if (wasConnected) {
      this.initiateChat();
      if (wasOpen && !isFullScreen) {
        this.setState({ isOpen: true });
      }
    }
  };

  componentDidUpdate(_, prevState: ChatContainerState) {
    // Persist some state to storage
    const { state } = this;
    if (state.isConnected !== prevState.isConnected) {
      saveWasConnected(state.isConnected);
    }
    if (state.isOpen !== prevState.isOpen) {
      saveWasOpen(state.isOpen);
      this.handleMessageReceipts();
    }
    if (state.transcript?.length !== prevState.transcript?.length) {
      this.handleMessageReceipts();
    }
  }

  componentDidMount() {
    // @ts-ignore
    import('amazon-connect-chatjs').then(() => {
      this.restoreFromStorage();
      this.setState({ isScriptReady: true });
    });

    this.props.router?.events?.on('routeChangeStart', this.handleRouteChangeStart);
    this.props.router?.events?.on('routeChangeComplete', this.handleRouteChangeComplete);
    window.addEventListener('focus', this.handleMessageReceipts);
  }

  componentWillUnmount() {
    this.props.router?.events?.off('routeChangeStart', this.handleRouteChangeStart);
    this.props.router?.events?.off('routeChangeComplete', this.handleRouteChangeComplete);
    window.removeEventListener('focus', this.handleMessageReceipts);
  }

  render() {
    const { canStartChat, isFullScreen } = this.props;
    const {
      attachmentScanning,
      isOpen,
      isConnected,
      isScriptReady,
      isInitiating,
      isAgentTyping,
      showFeedback,
      showQuitConfirmation,
      errorMessage,
      contactId,
      transcript,
      unreadCount
    } = this.state;
    const {
      handleClickOpen,
      handleClickClose,
      handleClickMinimize,
      handleDownloadAttachment,
      handleSendMessage,
      handleSendAttachment,
      handlePickerItemSelect,
      handleQuitConfirm,
      handleQuitCancel,
      handleUserTyping
    } = this;
    const isBusy = !isScriptReady || isInitiating;
    const filteredTranscript = withoutTriggeringMessages(transcript || []);
    const lastIncomingMessage = findLastIncomingMessage(filteredTranscript);

    const isAgentJoined = isAgentParticipating(filteredTranscript);
    // Need to traverse back the transcripts to find whether user is waiting for agents or not
    // handleMessage doesn't trigger when user refresh the page
    const isWaitingForAgent = !isAgentJoined && isConnected && isWaitingForAgentToJoin(filteredTranscript);

    const viewingTextMessage = lastIncomingMessage && !isInteractive(lastIncomingMessage);
    const textInputEnabled = !!(viewingTextMessage && !isBusy && isConnected && !isWaitingForAgent);
    const showLoading = isBusy || isAgentTyping || isWaitingForAgent;
    const chatAllowed = canStartChat || isOpen || isConnected;

    if (!isScriptReady || !chatAllowed) {
      return null;
    }

    return (
      <Chat
        isOpen={isOpen}
        isFullScreen={isFullScreen}
        isScanningAttachment={!!attachmentScanning}
        unreadCount={unreadCount}
        onTypeMessage={handleUserTyping}
        showLoading={showLoading}
        isAgentTyping={isAgentTyping}
        showQueuePosition={isWaitingForAgent}
        showFeedback={showFeedback}
        showTextInput={!showFeedback}
        textInputEnabled={textInputEnabled}
        canAddAttachment={isAgentJoined}
        errorMessage={errorMessage}
        contactId={contactId}
        transcript={filteredTranscript}
        showQuitConfirmation={showQuitConfirmation}
        downloadAttachment={handleDownloadAttachment}
        onQuitConfirm={handleQuitConfirm}
        onQuitCancel={handleQuitCancel}
        onClickOpen={handleClickOpen}
        onClickClose={handleClickClose}
        onClickMinimize={handleClickMinimize}
        onSendMessage={handleSendMessage}
        onSendAttachment={handleSendAttachment}
        onPickerItemSelect={handlePickerItemSelect}
      />
    );
  }
}

export const ChatRoot: React.FC = () => {
  const router = findParentNextRouter();
  const isMobile = useIsParentMobile();
  const canStartChat = useCanStartChat();
  const isSessionsV2Enabled = useIsSessionsV2Enabled();

  return (
    <ChatContainer
      router={router}
      isFullScreen={isMobile}
      canStartChat={canStartChat}
      isSessionsV2Enabled={isSessionsV2Enabled}
    />
  );
};

ChatRoot.displayName = 'ChatRoot';
